import { Avatar, Box, Drawer, IconButton, OutlinedInput, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import {
  Component,
  createRef,
  AnchorHTMLAttributes,
  ImgHTMLAttributes,
  isValidElement,
} from 'react';
import Markdown, { Components } from 'react-markdown';
import { KimEvent } from 'klayowebshared';
import { track } from '../common/track';
import { withLocale, WithLocaleProps } from '../providers/LocaleProvider';
import { AISparkleLoader } from './AISparkleLoader';
import { KitChart } from './KitChart';
import { KitMessage } from '../hooks/useKitChat';
import { isWhitelistedHelpUrl } from './kitLinkSafety';

const KIM_QUESTION_MAX_TRACKED_LENGTH = 256;

interface KitDrawerProps extends WithLocaleProps {
  onNavigate?: (path: string) => void;
}

interface KitDrawerState {
  open: boolean;
  expanded: boolean;
  messages: KitMessage[];
  isLoading: boolean;
  error: string | null;
  inputValue: string;
}

class KitDrawerBase extends Component<KitDrawerProps, KitDrawerState> {
  private messagesEndRef = createRef<HTMLDivElement>();

  constructor(props: KitDrawerProps) {
    super(props);
    this.state = {
      open: false,
      expanded: false,
      messages: [],
      isLoading: false,
      error: null,
      inputValue: '',
    };
  }

  open() {
    this.setState({ open: true });
  }

  close() {
    this.setState({ open: false });
  }

  toggleExpanded() {
    this.setState((s) => ({ expanded: !s.expanded }));
  }

  componentDidUpdate(_prevProps: KitDrawerProps, prevState: KitDrawerState) {
    if (
      this.state.messages.length > prevState.messages.length ||
      this.state.isLoading !== prevState.isLoading
    ) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    setTimeout(() => {
      this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }

  async handleSend() {
    const { inputValue, isLoading, messages } = this.state;
    if (isLoading || !inputValue.trim()) return;

    const text = inputValue.trim();
    const userMessage: KitMessage = { role: 'user', content: text };
    const optimisticMessages = [...messages, userMessage];

    this.setState({ inputValue: '', isLoading: true, error: null, messages: optimisticMessages });

    const baseProps = {
      length: text.length,
      questionNumber: messages.filter((m) => m.role === 'user').length + 1,
    };
    // Before the await, so an unanswered question still counts as asked. The text is truncated
    // (product decision 2026-10-05: the gist is enough) and sent on this event only; `length` stays
    // the untruncated size. Session recorders remain blocked by the attributes on the message list.
    track(KimEvent.Question, {
      ...baseProps,
      question: text.slice(0, KIM_QUESTION_MAX_TRACKED_LENGTH),
    });

    const startedAt = Date.now();
    let response;
    try {
      const axiosClient = (await import('../common/AxiosClient')).default;
      response = await axiosClient.post('/Kit/chat', { history: messages, message: text });
    } catch {
      this.setState({
        messages,
        isLoading: false,
        error: this.props.translate('kit.errorMessage'),
      });
      track(KimEvent.Failed, { ...baseProps, durationMs: Date.now() - startedAt });
      return;
    }

    this.setState({ messages: response.data.history, isLoading: false });
    track(KimEvent.Answered, { ...baseProps, durationMs: Date.now() - startedAt });
  }

  handleClear() {
    this.setState({ messages: [], error: null, inputValue: '' });
  }

  handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  }

  // NOTE: this <Markdown> pipeline intentionally uses NO remark/rehype plugins (no rehype-raw,
  // no remark-gfm). The a/img whitelists below are only complete while raw HTML is not rendered —
  // do not add a raw-HTML plugin without re-auditing every URL-bearing element.
  getMarkdownComponents(): Components {
    const { onNavigate } = this.props;
    return {
      code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
        if (className === 'language-chart') {
          return <KitChart json={String(children).trim()} />;
        }
        return <code className={className}>{children}</code>;
      },
      pre: ({ children }: { children?: React.ReactNode }) => {
        if (isValidElement(children) && children.type === KitChart) {
          return <>{children}</>;
        }
        return <pre>{children}</pre>;
      },
      a: ({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
        // In-app navigation: relative paths only. `!startsWith('//')` rejects protocol-relative
        // URLs like "//evil.com", which also begin with "/".
        if (href && href.startsWith('/') && !href.startsWith('//') && onNavigate) {
          return (
            <a
              href={href}
              {...rest}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(href);
              }}
            >
              {children}
            </a>
          );
        }
        // Absolute links: only whitelisted help.klayo.com URLs, opened safely in a new tab.
        // Everything else (incl. non-whitelisted absolute URLs and, when onNavigate is absent,
        // relative paths) renders as inert text — no clickable affordance.
        if (isWhitelistedHelpUrl(href)) {
          return (
            <a href={href} target='_blank' rel='noopener noreferrer' {...rest}>
              {children}
            </a>
          );
        }
        return <>{children}</>;
      },
      // Markdown ![](url) renders a live <img> that auto-fetches on render (no click) and bypasses
      // the anchor whitelist — a tracking/exfil vector. Apply the same host check; non-whitelisted
      // images render as inert alt text and are never fetched.
      img: ({ src, alt }: ImgHTMLAttributes<HTMLImageElement>) => {
        const srcStr = typeof src === 'string' ? src : undefined;
        if (isWhitelistedHelpUrl(srcStr)) {
          return <img src={srcStr} alt={alt ?? ''} style={{ maxWidth: '100%' }} />;
        }
        return <>{alt ?? ''}</>;
      },
    };
  }

  renderMessage(msg: KitMessage, index: number) {
    const isUser = msg.role === 'user';

    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          gap: 0.5,
        }}
      >
        {/* Sender label + avatar */}
        <Stack
          direction={isUser ? 'row-reverse' : 'row'}
          alignItems='center'
          spacing={0.75}
          sx={{ px: 0.5 }}
        >
          <Avatar
            sx={{
              width: 20,
              height: 20,
              bgcolor: isUser ? 'var(--grey-gr-400, #9CA3AF)' : 'var(--neon-blue-nb-400, #4d5ef7)',
              fontSize: '0.65rem',
            }}
          >
            {isUser ? (
              <PersonIcon sx={{ fontSize: 14 }} />
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 12 }} />
            )}
          </Avatar>
          <Typography
            variant='caption'
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '0.7rem',
              color: 'var(--grey-gr-500, #6B7280)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {isUser ? this.props.translate('kit.you') : this.props.translate('kit.title')}
          </Typography>
        </Stack>

        {/* Message bubble */}
        <Box
          sx={{
            maxWidth: '88%',
            px: 1.75,
            py: 1.25,
            borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            backgroundColor: isUser
              ? 'var(--neon-blue-nb-400, #4d5ef7)'
              : 'var(--grey-gr-50, #F9FAFB)',
            color: isUser ? '#fff' : 'var(--grey-gr-900, #111827)',
            border: isUser ? 'none' : '1px solid var(--grey-gr-200, #E5E7EB)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.8125rem',
            lineHeight: 1.6,
            wordBreak: 'break-word',
            ...(isUser
              ? { whiteSpace: 'pre-wrap' }
              : {
                  '& p, & ul, & ol, & li, & strong, & em, & a, & h1, & h2, & h3, & h4': {
                    fontFamily: 'Inter, sans-serif',
                  },
                  '& p': { m: 0 },
                  '& p + p': { mt: 1 },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& ul, & ol': { m: 0, pl: 2.5, '& li': { mb: 0.25 } },
                  '& code': {
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    backgroundColor: 'var(--grey-gr-100, #F3F4F6)',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: '4px',
                  },
                  '& pre': {
                    m: 0,
                    mt: 1,
                    p: 1.25,
                    borderRadius: '8px',
                    backgroundColor: 'var(--grey-gr-100, #F3F4F6)',
                    overflow: 'auto',
                    '& code': { backgroundColor: 'transparent', p: 0 },
                  },
                  '& h1, & h2, & h3, & h4': {
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    m: 0,
                    mt: 1,
                    mb: 0.5,
                  },
                  '& a': { color: 'var(--neon-blue-nb-400, #4d5ef7)', textDecoration: 'underline' },
                }),
          }}
        >
          {isUser ? (
            msg.content
          ) : (
            <Markdown components={this.getMarkdownComponents()}>{msg.content}</Markdown>
          )}
        </Box>
      </Box>
    );
  }

  render() {
    const { translate } = this.props;
    const { open, expanded, messages, isLoading, error, inputValue } = this.state;

    return (
      <Drawer
        open={open}
        anchor='right'
        onClose={() => this.close()}
        className='klayo-drawer'
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: expanded ? 640 : 380 },
            maxWidth: '100vw',
            height: {
              xs: '100vh',
              sm: expanded ? 'calc(100vh - 48px)' : 'min(680px, calc(100vh - 48px))',
            },
            maxHeight: '100vh',
            top: { xs: 0, sm: 'auto' },
            bottom: { xs: 'auto', sm: 24 },
            right: { xs: 0, sm: 24 },
            borderRadius: { xs: 0, sm: '16px' },
            boxShadow: '0 8px 40px rgba(0,0,0,0.16)',
          },
        }}
      >
        <Stack direction='column' sx={{ height: '100%', overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              background:
                'linear-gradient(135deg, var(--neon-blue-nb-400, #4d5ef7) 0%, var(--neon-blue-nb-600, #1634e6) 100%)',
              color: '#fff',
              px: 2.5,
              py: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: { xs: 0, sm: '16px 16px 0 0' },
              flexShrink: 0,
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.25}>
              <AutoAwesomeIcon sx={{ fontSize: 22 }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    lineHeight: 1.3,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {translate('kit.title')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.7rem',
                    opacity: 0.85,
                    lineHeight: 1.3,
                  }}
                >
                  {translate('kit.subtitle')}
                </Typography>
              </Box>
            </Stack>
            <Stack direction='row' spacing={0.25}>
              {messages.length > 0 && (
                <IconButton
                  size='small'
                  onClick={() => this.handleClear()}
                  title={translate('kit.clearChat')}
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
              <IconButton
                size='small'
                onClick={() => this.toggleExpanded()}
                title={translate(expanded ? 'kit.collapse' : 'kit.expand')}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' },
                }}
              >
                {expanded ? (
                  <CloseFullscreenIcon sx={{ fontSize: 18 }} />
                ) : (
                  <OpenInFullIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
              <IconButton
                size='small'
                onClick={() => this.close()}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Messages area. Suppressed from session recorders: the rendered conversation carries
              the colleague names and compliance detail we deliberately keep out of the analytics
              events, and Hotjar/PostHog replay capture DOM text regardless of what we capture. */}
          <Box
            data-testid='kit-messages'
            data-hj-suppress
            className='ph-no-capture'
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              px: 2,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: '#fff',
            }}
          >
            {/* Empty state */}
            {messages.length === 0 && !isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  px: 3,
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background:
                      'linear-gradient(135deg, rgba(77,94,247,0.1) 0%, rgba(77,94,247,0.05) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AISparkleLoader />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.8125rem',
                    color: 'var(--grey-gr-500, #6B7280)',
                    lineHeight: 1.6,
                    maxWidth: 260,
                  }}
                >
                  {translate('kit.emptyState')}
                </Typography>
              </Box>
            )}

            {/* Messages */}
            {messages.map((msg, index) => this.renderMessage(msg, index))}

            {/* Loading indicator */}
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.5,
                }}
              >
                <Stack direction='row' alignItems='center' spacing={0.75} sx={{ px: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      bgcolor: 'var(--neon-blue-nb-400, #4d5ef7)',
                      fontSize: '0.65rem',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                  </Avatar>
                  <Typography
                    variant='caption'
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      color: 'var(--grey-gr-500, #6B7280)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {translate('kit.title')}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: '14px 14px 14px 4px',
                    backgroundColor: 'var(--grey-gr-50, #F9FAFB)',
                    border: '1px solid var(--grey-gr-200, #E5E7EB)',
                  }}
                >
                  <AISparkleLoader />
                </Box>
              </Box>
            )}

            {/* Error message */}
            {error && (
              <Box
                sx={{
                  mx: 'auto',
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  backgroundColor: 'var(--torch-red-tr-50, #FEF2F2)',
                  border: '1px solid var(--torch-red-tr-200, #FECACA)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    color: 'var(--torch-red-tr-600, #DC2626)',
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}

            <div ref={this.messagesEndRef} />
          </Box>

          {/* Input area */}
          <Box
            sx={{
              borderTop: '1px solid var(--grey-gr-200, #E5E7EB)',
              p: 1.5,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              backgroundColor: '#fff',
              flexShrink: 0,
              borderRadius: { xs: 0, sm: '0 0 16px 16px' },
            }}
          >
            <OutlinedInput
              fullWidth
              size='small'
              data-hj-suppress
              className='ph-no-capture'
              placeholder={translate('kit.inputPlaceholder')}
              value={inputValue}
              onChange={(e) => this.setState({ inputValue: e.target.value })}
              onKeyDown={(e) => this.handleKeyDown(e)}
              disabled={isLoading}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8125rem',
                borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--grey-gr-200, #E5E7EB)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--grey-gr-300, #D1D5DB)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--neon-blue-nb-400, #4d5ef7)',
                },
              }}
            />
            <IconButton
              onClick={() => this.handleSend()}
              disabled={isLoading || !inputValue.trim()}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                backgroundColor: 'var(--neon-blue-nb-400, #4d5ef7)',
                color: '#fff',
                '&:hover': { backgroundColor: 'var(--neon-blue-nb-500, #1b3df2)' },
                '&.Mui-disabled': {
                  backgroundColor: 'var(--grey-gr-100, #F3F4F6)',
                  color: 'var(--grey-gr-400, #9CA3AF)',
                },
                transition: 'background-color 0.15s ease',
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Stack>
      </Drawer>
    );
  }
}

export const KitDrawer = withLocale(KitDrawerBase);
